import Feature from "ol/Feature";
import { Point } from "ol/geom";
import { AcNode, DIV, emptyElement, BUTTON, A, NODISPLAY } from "./dom";
import { DataProps, PropKey } from "./state";
import { fromRecord, parseYearFromDate, tr, tr_url, trMulti } from "./locale";
import { fromNullable, Option } from "./option";
import { Base, isArray, isRecord } from "./util";
import type { DataContext } from "./index";
import { View } from "ol";

const renderZoom = (f: Feature, view: View) => {
  const zoomBtn = (center: number[], view: View) =>
    BUTTON("zoom", () => view.animate({ center, zoom: 18 }), "zoom");

  const point = <Point>f.getGeometry();
  const zoom = fromNullable(point).map((geom) =>
    zoomBtn(geom.getFlatCoordinates(), view),
  );
  return zoom;
};

const renderString = (key: PropKey, props: Option<DataProps>): AcNode => {
  const valueOption = props.chain((p) => fromNullable(p[key]));
  return valueOption.map((s) =>
    DIV(`kv ${key}`, DIV(`value ${typeof s}`, trMulti(s.toString()))),
  );
};

const renderStringIfMeaningful = (
  key: PropKey,
  props: Option<DataProps>,
): AcNode => {
  const valueOption = props.chain((p) => fromNullable(p[key]));
  const string = valueOption.map((s) => trMulti(s.toString()));
  return string.map((s) =>
    s.trimStart().toLowerCase() == "autre"
      ? NODISPLAY()
      : DIV(`kv ${key}`, DIV(`value ${typeof s}`, s)),
  );
};

const renderDate = (key: PropKey, props: Option<DataProps>) => {
  const date = props.chain((p) =>
    fromNullable(p[key]).chain((d) => fromNullable(parseYearFromDate(d))),
  );
  return date.map((d) =>
    DIV(`kv ${key}`, DIV(`value ${typeof d}`, trMulti("creation_date"), d)),
  );
};
const renderRecord = (key: PropKey, props: Option<DataProps>) => {
  const valueOption = props.chain((p) => fromNullable(p[key]));
  return valueOption.chain((record) =>
    isRecord(record).map((r) =>
      DIV(`kv ${key}`, DIV(`value record`, fromRecord(r))),
    ),
  );
};

const renderPrograms = (programsOption: Option<string[]>): AcNode =>
  programsOption.chain((programs) =>
    isArray(programs).map((a) =>
      DIV(
        `kv program`,
        ...a.map((element) => DIV(`element ${element}`, tr(element))),
      ),
    ),
  );

const renderUrl = (urlOption: Option<Base>) =>
  urlOption.map((url) =>
    DIV(`kv url`, DIV(`value url`, A("link", tr_url(url.toString()), tr("more")))),
  );

export const featureInfoFactory =
  (parent: Element, dataContext: DataContext) =>
  (features: Feature[], view: View, clearSelection: () => void) => {
    emptyElement(parent);
    features.map((feature) => {
      const props: Option<DataProps> = dataContext.getProps(feature);

      const program = props.chain((p) => fromNullable(p["program"]));
      const url = props.chain((p) => fromNullable(p["url"]));
      const featureElem = DIV(
        "feature-wrapper",
        BUTTON("close", clearSelection),
        renderPrograms(program),
        renderString("name", props),
        renderZoom(feature, view),
        renderStringIfMeaningful("sector", props),
        renderStringIfMeaningful("structure_type", props),
        renderDate("creation_date", props),
        renderRecord("description", props),
        renderUrl(url), //TODO: make translation!!!
      );
      parent.appendChild(featureElem);
    });
  };
